import { useCallback, useMemo } from 'react';
import { useInfo } from '../../../Flow/ConsentContextProvider';
import type { BitbucketWorkspace } from '#definitions/consent-info';
import {
  buildMultiSelectOrgIds,
  buildSandboxSiteUrls,
  buildSiteOrgIndex,
  getBitbucketWorkspaceOrgId,
  getSelectionRules,
  isSandboxSite,
  resolveSiteOrgId,
  type SiteOrgIndex,
} from './orgMatch';

export interface UseOrgMatchArgs {
  /**
   * Whether the site and Bitbucket pickers each hold several selections. A
   * pick then adds to a field's selection rather than replacing it, so the
   * field's own selection constrains it too.
   */
  isMultiSelect: boolean;
  /** Only a picker on screen takes part: a hidden selection is not a choice. */
  showSiteSelector: boolean;
  showBitbucketSelector: boolean;
  /** The selected Atlassian site URLs, in selection order. */
  selectedSiteUrls: readonly string[];
  /** The selected Bitbucket workspaces, each carrying its own orgId. */
  selectedBitbucketWorkspaces: readonly BitbucketWorkspace[];
}

export interface OrgMatch {
  /** The org a further site pick has to belong to, or null when unconstrained. */
  siteConstraintOrgId: string | null;
  /** The org a further workspace pick has to belong to, or null when unconstrained. */
  bitbucketConstraintOrgId: string | null;
  /** Two known orgs differ, so the selections cannot be granted together. */
  hasOrgMismatch: boolean;
  /**
   * Site URL → owning orgId from `/consent/info`. Built once here so selectors
   * can look a site up without indexing the response themselves.
   */
  siteOrgIndex: SiteOrgIndex;
  /**
   * Whether the selection's org lets a picker hold several selections. True
   * while nothing is selected; false once the selection's org is not known to
   * allow it (fail closed), or when multi-select is off altogether.
   */
  isMultiSelectionAllowed: boolean;
  /** Whether `/consent/info` flags a site URL as a sandbox. */
  isSandboxSiteUrl: (siteUrl: string) => boolean;
  /**
   * Which kind of site a further site pick has to be: true = sandbox only,
   * false = production only, null = unconstrained.
   */
  siteSandboxConstraint: boolean | null;
  /** A sandbox site is selected, so Bitbucket workspaces cannot be granted. */
  isBitbucketBlockedBySandbox: boolean;
  /** A sandbox site and a Bitbucket workspace are both selected; blocks Accept. */
  hasSandboxConflict: boolean;
}

/** The orgs among a picker's selections, dropping the unknown: they fail open. */
const knownOrgIds = (orgIds: readonly (string | null)[]): string[] =>
  orgIds.filter((orgId): orgId is string => orgId != null);

/**
 * Keeps the site and Bitbucket selections within one organization.
 *
 * A consent grants a single organization's data, so every selection on screen
 * has to share an org. In single-select a picker is constrained by the other
 * picker alone, as a pick replaces its own; in multi-select by every selection.
 * The selectors report a conflict and Accept is blocked until a selection
 * changes. Unknown orgs fail open, as `isOrgMismatch` does.
 */
export function useOrgMatch({
  isMultiSelect,
  showSiteSelector,
  showBitbucketSelector,
  selectedSiteUrls,
  selectedBitbucketWorkspaces,
}: UseOrgMatchArgs): OrgMatch {
  const info = useInfo();

  const siteOrgIndex = useMemo(
    () => buildSiteOrgIndex(info),
    [info.availableOrganizations],
  );

  const multiSelectOrgIds = useMemo(
    () => buildMultiSelectOrgIds(info),
    [info.availableOrganizations],
  );
  const sandboxSiteUrls = useMemo(
    () => buildSandboxSiteUrls(info),
    [info.availableOrganizations],
  );
  const isSandboxSiteUrl = useCallback(
    (siteUrl: string) => isSandboxSite(sandboxSiteUrls, siteUrl),
    [sandboxSiteUrls],
  );

  return useMemo(() => {
    const shownSiteUrls = showSiteSelector ? selectedSiteUrls : [];
    const shownWorkspaces = showBitbucketSelector
      ? selectedBitbucketWorkspaces
      : [];
    const siteOrgIds = knownOrgIds(
      shownSiteUrls.map((url) => resolveSiteOrgId(siteOrgIndex, url)),
    );
    const bitbucketOrgIds = knownOrgIds(
      shownWorkspaces.map(getBitbucketWorkspaceOrgId),
    );
    const selectionOrgId = siteOrgIds[0] ?? bitbucketOrgIds[0] ?? null;

    const {
      isMultiSelectionAllowed,
      siteSandboxConstraint,
      isBitbucketBlockedBySandbox,
      hasSandboxConflict,
    } = getSelectionRules({
      isMultiSelect,
      selectedSiteUrls: shownSiteUrls,
      hasSelectedWorkspaces: shownWorkspaces.length > 0,
      selectionOrgId,
      multiSelectOrgIds,
      sandboxSiteUrls,
    });

    return {
      siteConstraintOrgId: isMultiSelect
        ? selectionOrgId
        : (bitbucketOrgIds[0] ?? null),
      bitbucketConstraintOrgId: isMultiSelect
        ? selectionOrgId
        : (siteOrgIds[0] ?? null),
      hasOrgMismatch: new Set([...siteOrgIds, ...bitbucketOrgIds]).size > 1,
      siteOrgIndex,
      isMultiSelectionAllowed,
      isSandboxSiteUrl,
      siteSandboxConstraint,
      isBitbucketBlockedBySandbox,
      hasSandboxConflict,
    };
  }, [
    isMultiSelect,
    showSiteSelector,
    showBitbucketSelector,
    selectedSiteUrls,
    selectedBitbucketWorkspaces,
    siteOrgIndex,
    multiSelectOrgIds,
    sandboxSiteUrls,
    isSandboxSiteUrl,
  ]);
}
